import { Text } from '@thanx/uikit/text'
import { PermissionResource } from 'api/roles'
import useFlag from 'hooks/useFlag'
import { buildTranslate } from 'locales'
import { useMemo, useState } from 'react'
import {
  PermissionRow,
  PermissionValue,
  PERMISSION_OPTIONS,
  PERMISSION_OPTION_GAP,
} from './PermissionRow'

const t = buildTranslate('dashboard_access.edit_role')

export type LockedResourceConfig = {
  lockedOptions: Set<PermissionValue>
  tooltip: string
}

type Subsection = {
  title: string
  description: string
  resourceNames: string[]
}

/**
 * Subsections split a flat resource list into labelled groups.
 * Only the "marketing" group uses this today.
 */
const tSub = buildTranslate('dashboard_access.edit_role.subsections')

const GROUP_SUBSECTIONS: Record<string, Subsection[]> = {
  marketing: [
    {
      title: tSub('campaigns.title'),
      description: tSub('campaigns.description'),
      resourceNames: [
        'campaigns',
        'campaign_collaboration',
        'campaign_approvals',
      ],
    },
    {
      title: tSub('segments.title'),
      description: tSub('segments.description'),
      resourceNames: ['segments', 'segment_collaboration'],
    },
  ],
}

type Props = {
  category: string
  groupName?: string
  resources: PermissionResource[]
  permissions: Record<string, PermissionValue>
  onChange: (key: string, value: PermissionValue) => void
  disabled: boolean
  lockedResources?: Record<string, LockedResourceConfig>
}

export const PermissionSection = ({
  category,
  groupName,
  resources,
  permissions,
  onChange,
  disabled,
  lockedResources,
}: Props) => {
  const [expanded, setExpanded] = useState(true)
  const showCampaignWarning = useFlag('rbac-warning-message', false)

  const groupValue = useMemo<PermissionValue | null>(() => {
    if (!resources.length) return null
    const first = permissions[resources[0].name] ?? 'no_access'
    const allSame = resources.every(
      r => (permissions[r.name] ?? 'no_access') === first
    )
    return allSame ? first : null
  }, [resources, permissions])

  const handleBulkChange = (value: PermissionValue) => {
    resources.forEach(resource => {
      const locked = lockedResources?.[resource.name]
      if (locked?.lockedOptions.has(value)) return
      onChange(resource.name, value)
    })
  }

  const subsections = groupName ? GROUP_SUBSECTIONS[groupName] : undefined

  const renderResource = (resource: PermissionResource, isLast: boolean) => {
    const locked = lockedResources?.[resource.name]
    return (
      <PermissionRow
        key={resource.name}
        resource={resource}
        value={permissions[resource.name] ?? 'no_access'}
        onChange={onChange}
        disabled={disabled}
        isLast={isLast}
        lockedOptions={locked?.lockedOptions}
        lockedTooltip={locked?.tooltip}
      />
    )
  }

  const renderResources = () => {
    if (!subsections) {
      return resources.map((resource, index) =>
        renderResource(resource, index === resources.length - 1)
      )
    }

    // Partition resources into subsections and a remainder bucket
    const subsectionNames = new Set(subsections.flatMap(s => s.resourceNames))
    const remainder = resources.filter(r => !subsectionNames.has(r.name))

    return (
      <>
        {subsections.map(subsection => {
          const subsectionResources = subsection.resourceNames
            .map(name => resources.find(r => r.name === name))
            .filter((r): r is PermissionResource => r !== undefined)
          if (!subsectionResources.length) return null

          return (
            <div key={subsection.title} className="mb-m">
              <div className="pt-m pb-xs border-bottom-1 grey-20-border">
                <Text.BodyText3 className="m-0 d-block grey-90">
                  {subsection.title}
                </Text.BodyText3>
                <div className="mt-xxs mb-xs">
                  {subsection.description
                    .split(/\.\s+/)
                    .filter(Boolean)
                    .map((sentence, i) => (
                      <Text.BodyText4
                        key={i}
                        color="grey70"
                        className="m-0 d-block"
                      >
                        {sentence.endsWith('.') ? sentence : `${sentence}.`}
                      </Text.BodyText4>
                    ))}
                </div>
              </div>
              <div className="pl-4">
                {subsectionResources.map((resource, index) =>
                  renderResource(
                    resource,
                    index === subsectionResources.length - 1
                  )
                )}
              </div>
            </div>
          )
        })}
        {remainder.map((resource, index) =>
          renderResource(resource, index === remainder.length - 1)
        )}
      </>
    )
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center py-s border-bottom-1 grey-20-border">
        <button
          type="button"
          className="d-flex align-items-center bg-transparent border-0 px-0 cursor-pointer"
          onClick={() => setExpanded(!expanded)}
          aria-expanded={expanded}
        >
          <i
            className={`fa fa-chevron-${
              expanded ? 'down' : 'right'
            } grey-50 font-size-14 mr-xs`}
            aria-hidden="true"
          />
          <Text.Header4 className="m-0">{category}</Text.Header4>
        </button>
        <div
          className="d-flex align-items-center"
          style={{ gap: PERMISSION_OPTION_GAP }}
        >
          {PERMISSION_OPTIONS.map(option => (
            <label
              key={option.val}
              className="d-flex align-items-center m-0 cursor-pointer"
            >
              <input
                type="radio"
                name={`bulk-${category}`}
                checked={groupValue === option.val}
                onChange={() => handleBulkChange(option.val)}
                disabled={disabled}
                className="mr-xs"
              />
              <Text.BodyText4 color="grey70" className="m-0">
                {option.label}
              </Text.BodyText4>
            </label>
          ))}
        </div>
      </div>
      {expanded && (
        <div className="pl-4">
          {showCampaignWarning && category === 'Marketing' && (
            <div
              className="d-flex align-items-start p-s mt-s mb-xs"
              style={{ backgroundColor: '#FFF8E1', borderRadius: 4 }}
            >
              <i
                className="fa fa-exclamation-triangle mr-xs mt-xxs"
                aria-hidden="true"
                style={{ color: '#F9A825' }}
              />
              <Text.BodyText4 color="grey70" className="m-0">
                {t('campaign_warning_banner')}
              </Text.BodyText4>
            </div>
          )}
          {renderResources()}
        </div>
      )}
    </div>
  )
}
